import ArrowBack from '@mui/icons-material/ArrowBack';
import RestartAlt from '@mui/icons-material/RestartAlt';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  useGetGuestJourneySettingsDefaultsQuery,
  useGetGuestJourneySettingsQuery,
  useResetGuestJourneySettingsMutation,
  useUpdateGuestJourneySettingsMutation,
} from 'api/client-domain/client-domain';
import { GuestJourneySettings } from 'api/reports-service/guest-journey-reports.types';
import {
  GUEST_JOURNEY_STAGE_SLIDER_MAX,
  centAbove,
  isAtDefaultThresholds,
} from 'api/reports-service/guest-journey-settings.utils';
import DualRangeSlider from 'components/dual-range-slider/dual-range-slider.component';
import IncPage from 'components/inc-page/inc-page.component';
import IncPageBody from 'components/inc-page/inc-page-body/inc-page-body.component';
import IncPageHeader from 'components/inc-page/inc-page-header/inc-page-header.component';
import IncPageTitle from 'components/inc-page/inc-page-title/inc-page-title.component';
import { useHasPermission } from 'hooks/authorization.hooks';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGuestJourneySettingsForm } from './guest-journey-settings.hooks';
import GuestJourneyDefaultThresholdsTable from './guest-journey-default-thresholds-table.component';
import GuestJourneySettingsHistory from './guest-journey-settings-history.component';
import GuestJourneySettingsProcessingNotice from './guest-journey-settings-processing-notice.component';
import GuestJourneyStageNameField from './guest-journey-stage-name-field.component';

const VISIT_SEGMENT_COLORS = ['#D7E9EC', '#7CC4CB', '#2A8A8F'] as const;
const SPEND_SEGMENT_COLORS = ['#F3D9C9', '#E8A07A', '#C8643A'] as const;
const TIERS = [1, 2, 3] as const;
const SET_OPTS = { shouldValidate: true, shouldDirty: true } as const;

/**
 * Route element for `guest-journey/settings`. Gates the editor on the settings
 * load (same as the report page) so the form seeds from real data exactly once.
 */
const GuestJourneySettingsPage = () => {
  const { t } = useTranslation();
  const { data, isFetching, isError, refetch } =
    useGetGuestJourneySettingsQuery();

  if (data) return <GuestJourneySettingsEditor initialSettings={data} />;

  return (
    <IncPage>
      <IncPageHeader>
        <IncPageTitle>{t('page.guestJourney.settings.title')}</IncPageTitle>
      </IncPageHeader>
      <IncPageBody>
        {isFetching ? (
          <Stack
            alignItems="center"
            paddingY={8}
            data-testid="GuestJourney.Settings.Loading"
          >
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Alert
            severity="error"
            data-testid="GuestJourney.Settings.LoadError"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t('page.guestJourney.settings.loadError.retry')}
              </Button>
            }
          >
            {t('page.guestJourney.settings.loadError.message')}
          </Alert>
        ) : null}
      </IncPageBody>
    </IncPage>
  );
};

type EditorProps = {
  initialSettings: GuestJourneySettings;
};

const maxWidth = 900;

const GuestJourneySettingsEditor = ({ initialSettings }: EditorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Without edit rights the page is a read-only view of the current settings.
  const canEdit = useHasPermission('EDIT_GUEST_JOURNEY_SETTINGS');
  const form = useGuestJourneySettingsForm(initialSettings);
  const { watch, setValue, handleSubmit, formState, reset: resetForm } = form;

  const [showHistory, setShowHistory] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [save, { isLoading: saving }] = useUpdateGuestJourneySettingsMutation();
  const [resetSettings, { isLoading: resetting }] =
    useResetGuestJourneySettingsMutation();

  // Recommended default boundaries — drive the default tables, slider ticks, and
  // Reset. The card shows a spinner while they load and an error+retry if the
  // fetch fails; Reset stays disabled until they resolve.
  const {
    data: defaults,
    isError: defaultsError,
    refetch: refetchDefaults,
  } = useGetGuestJourneySettingsDefaultsQuery();

  // Live form values drive the sliders and default tables.
  const values = watch();
  const atDefaults = !!defaults && isAtDefaultThresholds(values, defaults);
  const hasValidationErrors = Object.keys(formState.errors).length > 0;

  // Recommended default ranges shown in the two basic tables. The boundaries are
  // the fetched defaults; the stage names track the current (editable) labels.
  // Empty until the defaults load (the card shows a spinner in that window).
  const defaultVisitRows = defaults
    ? [
        {
          name: values.visitTier1Label,
          range: t('page.guestJourney.settings.defaults.visitRange', {
            min: 1,
            max: defaults.visitTier1Threshold,
          }),
        },
        {
          name: values.visitTier2Label,
          range: t('page.guestJourney.settings.defaults.visitRange', {
            min: defaults.visitTier1Threshold + 1,
            max: defaults.visitTier2Threshold,
          }),
        },
        {
          name: values.visitTier3Label,
          range: t('page.guestJourney.settings.defaults.visitRangeOpen', {
            min: defaults.visitTier2Threshold + 1,
          }),
        },
      ]
    : [];
  const defaultSpendRows = defaults
    ? [
        {
          name: values.spendTier1Label,
          range: t('page.guestJourney.settings.defaults.spendRange', {
            min: (0).toFixed(2),
            max: defaults.spendTier1Threshold.toFixed(2),
          }),
        },
        {
          name: values.spendTier2Label,
          range: t('page.guestJourney.settings.defaults.spendRange', {
            min: centAbove(defaults.spendTier1Threshold).toFixed(2),
            max: defaults.spendTier2Threshold.toFixed(2),
          }),
        },
        {
          name: values.spendTier3Label,
          range: t('page.guestJourney.settings.defaults.spendRangeOpen', {
            min: centAbove(defaults.spendTier2Threshold).toFixed(2),
          }),
        },
      ]
    : [];

  const setVisitBounds = (lower: number, upper: number) => {
    setValue('visitTier1Threshold', lower, SET_OPTS);
    setValue('visitTier2Threshold', upper, SET_OPTS);
  };
  const setSpendBounds = (lower: number, upper: number) => {
    setValue('spendTier1Threshold', lower, SET_OPTS);
    setValue('spendTier2Threshold', upper, SET_OPTS);
  };

  const goBack = () => navigate('..', { relative: 'path' });

  // Only validated settings reach the BE — handleSubmit blocks an invalid save.
  const onSave = handleSubmit(async submitted => {
    setSaveError(false);
    try {
      await save(submitted).unwrap();
      goBack();
    } catch {
      setSaveError(true);
    }
  });

  // Reset reverts the thresholds server-side (the BE keeps the tier names and
  // records a history entry) and re-seeds the form from the returned settings —
  // no separate Save. The user stays on the page to keep editing or leave.
  const onReset = async () => {
    setResetError(false);
    try {
      resetForm(await resetSettings().unwrap());
    } catch {
      setResetError(true);
    }
  };

  const formatVisit = (value: number) => String(value);
  const formatSpend = (value: number) => `$${value.toFixed(2)}`;

  return (
    <FormProvider {...form}>
      <IncPage>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          maxWidth={maxWidth}
          alignSelf="center"
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={goBack}
              aria-label={t('page.guestJourney.settings.back')}
              data-testid="GuestJourney.Settings.Back"
            >
              <ArrowBack />
            </IconButton>
            <IncPageTitle>{t('page.guestJourney.settings.title')}</IncPageTitle>
          </Stack>
          <Button
            variant="text"
            onClick={() => setShowHistory(prev => !prev)}
            data-testid="GuestJourney.Settings.ViewChanges"
          >
            {t('page.guestJourney.settings.viewChanges')}
          </Button>
        </Stack>

        <IncPageBody>
          <Stack
            spacing={2}
            width="100%"
            maxWidth={maxWidth}
            mx="auto"
            data-testid="GuestJourney.Settings"
          >
            <GuestJourneySettingsProcessingNotice />

            {!canEdit && (
              <Alert
                severity="info"
                data-testid="GuestJourney.Settings.ViewOnly"
              >
                {t('page.guestJourney.settings.viewOnly')}
              </Alert>
            )}
            {resetError && (
              <Alert
                severity="error"
                data-testid="GuestJourney.Settings.ResetError"
              >
                {t('page.guestJourney.settings.resetError')}
              </Alert>
            )}

            {saveError && (
              <Alert
                severity="error"
                data-testid="GuestJourney.Settings.SaveError"
              >
                {t('page.guestJourney.settings.saveError')}
              </Alert>
            )}

            {hasValidationErrors && (
              <Alert
                severity="error"
                data-testid="GuestJourney.Settings.ValidationError"
              >
                {t('page.guestJourney.settings.validationError')}
              </Alert>
            )}

            {showHistory && <GuestJourneySettingsHistory />}

            <Card data-testid="GuestJourney.Settings.Defaults">
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  spacing={2}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="h2">
                      {t('page.guestJourney.settings.defaults.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('page.guestJourney.settings.defaults.description')}
                    </Typography>
                  </Stack>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<RestartAlt />}
                      onClick={onReset}
                      disabled={!defaults || atDefaults || resetting}
                      data-testid="GuestJourney.Settings.Reset"
                    >
                      {t('page.guestJourney.settings.defaults.reset')}
                    </Button>
                  )}
                </Stack>
                {defaults ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={4}
                    mt={2}
                  >
                    <GuestJourneyDefaultThresholdsTable
                      data-testid="GuestJourney.Settings.Defaults.VisitTable"
                      title={t(
                        'page.guestJourney.settings.defaults.visitStages',
                      )}
                      rows={defaultVisitRows}
                    />
                    <GuestJourneyDefaultThresholdsTable
                      data-testid="GuestJourney.Settings.Defaults.SpendTable"
                      title={t(
                        'page.guestJourney.settings.defaults.averageCheckStages',
                      )}
                      rows={defaultSpendRows}
                    />
                  </Stack>
                ) : defaultsError ? (
                  <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    data-testid="GuestJourney.Settings.Defaults.LoadError"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => refetchDefaults()}
                      >
                        {t('page.guestJourney.settings.loadError.retry')}
                      </Button>
                    }
                  >
                    {t('page.guestJourney.settings.defaults.loadError')}
                  </Alert>
                ) : (
                  <Stack
                    alignItems="center"
                    py={2}
                    data-testid="GuestJourney.Settings.Defaults.Loading"
                  >
                    <CircularProgress size={24} />
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card data-testid="GuestJourney.Settings.VisitStages">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h2">
                    {t('page.guestJourney.settings.visitStages.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('page.guestJourney.settings.visitStages.description', {
                      tier1: values.visitTier1Label,
                      tier3: values.visitTier3Label,
                    })}
                  </Typography>
                  <DualRangeSlider
                    data-testid="GuestJourney.Settings.VisitSlider"
                    min={1}
                    max={GUEST_JOURNEY_STAGE_SLIDER_MAX}
                    // Explorer may be a single visit ("1-1"), so the lower handle
                    // can reach the min (1). Spend keeps its default floor.
                    allowLowerAtMin
                    lower={values.visitTier1Threshold}
                    upper={values.visitTier2Threshold}
                    onChange={setVisitBounds}
                    disabled={!canEdit}
                    format={formatVisit}
                    ariaPrefix={t(
                      'page.guestJourney.settings.visitStages.title',
                    )}
                    ticks={
                      defaults && [
                        defaults.visitTier1Threshold,
                        defaults.visitTier2Threshold,
                      ]
                    }
                    segments={[
                      {
                        label: values.visitTier1Label,
                        color: VISIT_SEGMENT_COLORS[0],
                      },
                      {
                        label: values.visitTier2Label,
                        color: VISIT_SEGMENT_COLORS[1],
                      },
                      {
                        label: values.visitTier3Label,
                        color: VISIT_SEGMENT_COLORS[2],
                      },
                    ]}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {TIERS.map(tier => (
                      <GuestJourneyStageNameField
                        key={tier}
                        name={`visitTier${tier}Label`}
                        label={t('page.guestJourney.settings.stageName')}
                        readOnly={!canEdit}
                        data-testid={`GuestJourney.Settings.VisitName.tier${tier}`}
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card data-testid="GuestJourney.Settings.AverageCheckStages">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h2">
                    {t('page.guestJourney.settings.averageCheck.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('page.guestJourney.settings.averageCheck.description', {
                      tier1: values.spendTier1Label,
                      tier3: values.spendTier3Label,
                    })}
                  </Typography>
                  <DualRangeSlider
                    data-testid="GuestJourney.Settings.SpendSlider"
                    min={0}
                    max={GUEST_JOURNEY_STAGE_SLIDER_MAX}
                    boundaryStep={0.01}
                    lower={values.spendTier1Threshold}
                    upper={values.spendTier2Threshold}
                    onChange={setSpendBounds}
                    disabled={!canEdit}
                    format={formatSpend}
                    prefix="$"
                    ariaPrefix={t(
                      'page.guestJourney.settings.averageCheck.title',
                    )}
                    ticks={
                      defaults && [
                        defaults.spendTier1Threshold,
                        defaults.spendTier2Threshold,
                      ]
                    }
                    segments={[
                      {
                        label: values.spendTier1Label,
                        color: SPEND_SEGMENT_COLORS[0],
                      },
                      {
                        label: values.spendTier2Label,
                        color: SPEND_SEGMENT_COLORS[1],
                      },
                      {
                        label: values.spendTier3Label,
                        color: SPEND_SEGMENT_COLORS[2],
                      },
                    ]}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {TIERS.map(tier => (
                      <GuestJourneyStageNameField
                        key={tier}
                        name={`spendTier${tier}Label`}
                        label={t('page.guestJourney.settings.stageName')}
                        readOnly={!canEdit}
                        data-testid={`GuestJourney.Settings.SpendName.tier${tier}`}
                      />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Box
              display="flex"
              justifyContent="flex-end"
              gap={2}
              data-testid="GuestJourney.Settings.Footer"
            >
              <Button
                variant="outlined"
                onClick={goBack}
                data-testid="GuestJourney.Settings.Cancel"
              >
                {canEdit
                  ? t('page.guestJourney.settings.footer.cancel')
                  : t('page.guestJourney.settings.back')}
              </Button>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={saving}
                  data-testid="GuestJourney.Settings.Save"
                >
                  {t('page.guestJourney.settings.footer.save')}
                </Button>
              )}
            </Box>
          </Stack>
        </IncPageBody>
      </IncPage>
    </FormProvider>
  );
};

export { GuestJourneySettingsEditor };
export default GuestJourneySettingsPage;
